import { type ClassValue, clsx } from "clsx";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { z } from "zod";

import { auth } from "~/firebase.client";
import { signInWithCustomToken } from "firebase/auth";
import { useSubmit } from "@remix-run/react";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage"; // New Firebase imports

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function useOutsideClick(ref: any, callback: () => void) {
  useEffect(() => {
    // Function to detect click event
    function handleClickOutside(event: any) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    }

    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback]);
}

export type GeolocationResponse = {
  ip: string;
  city: string;
  countryName: string;
  countryCode: string;
  regionName: string;
  timezone: string;
  lat: number;
  lon: number;
};

type IpApiResponse = {
  as: string;
  city: string;
  country: string;
  countryCode: string;
  isp: string;
  lat: number;
  lon: number;
  org: string;
  query: string;
  region: string;
  regionName: string;
  status: string;
  timezone: string;
  zip: string;
};

type IpapiCoResponse = {
  ip: string;
  network: string;
  version: string;
  city: string;
  region: string;
  region_code: string;
  country: string;
  country_name: string;
  country_code: string;
  country_code_iso3: string;
  country_capital: string;
  country_tld: string;
  continent_code: string;
  in_eu: boolean;
  postal: string;
  latitude: number;
  longitude: number;
  timezone: string;
  utc_offset: string;
  country_calling_code: string;
  currency: string;
  currency_name: string;
  languages: string;
  country_area: number;
  country_population: number;
  asn: string;
  org: string;
};

function normalizeIpApiResponse(data: IpApiResponse): GeolocationResponse {
  return {
    ip: data.query,
    city: data.city,
    countryName: data.country,
    countryCode: data.countryCode,
    regionName: data.regionName,
    timezone: data.timezone,
    lat: data.lat,
    lon: data.lon,
  };
}

function normalizeIpapiCoResponse(data: IpapiCoResponse): GeolocationResponse {
  return {
    ip: data.ip,
    city: data.city,
    countryName: data.country_name,
    countryCode: data.country_code,
    regionName: data.region,
    timezone: data.timezone,
    lat: data.latitude,
    lon: data.longitude,
  };
}
//for future Xforwarded geoLocation update, pass in the IP adress as a prop to this function, and  it shoyld return the geo , so update this function prop when time is relevant
export async function getGeolocation(): Promise<
  GeolocationResponse | undefined
> {
  try {
    // Try ip-api.com first
    const ipApiResponse = await fetch(
      "https://pro.ip-api.com/json/?key=I9Cd2AbL64QbZsN"
    );

    if (ipApiResponse.ok) {
      const data = await ipApiResponse.json();
      return normalizeIpApiResponse(data);
    }

    // If first API fails, try ipapi.co
    const ipapiCoResponse = await fetch("https://ipapi.co/json/");

    if (ipapiCoResponse.ok) {
      const data = await ipapiCoResponse.json();
      return normalizeIpapiCoResponse(data);
    }

    return undefined;
  } catch (error) {
    console.error("Failed to fetch geolocation data:", error);
    return undefined;
  }
}

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value); // Update debounced value after delay
    }, delay);

    // Cleanup the timeout if value changes before the delay completes
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

export function useSignInWithCustomToken(
  token?: string | null,
  redirectTo?: string | null
) {
  const submit = useSubmit();
  useEffect(() => {
    if (auth && token) {
      signInWithCustomToken(auth, token).then((userCredential) => {
        userCredential.user.getIdToken().then((idToken) => {
          submit(
            {
              idToken,
              redirectTo: redirectTo || null,
            },
            { method: "post", action: "/auth/login" }
          );
        });
      });
    }
  }, [auth, token, submit, redirectTo]); // Added submit and redirectTo to dependency array
}

export type FieldErrors = Record<string, string[]>;

export function validateForm<T extends z.ZodTypeAny>(
  formData: FormData,
  schema: T
):
  | {
      data: z.infer<T>;
      errors: null;
    }
  | {
      data: null;
      errors: FieldErrors;
    } {
  const result = schema.safeParse(Object.fromEntries(formData));

  if (result.success) {
    return { data: result.data, errors: null };
  } else {
    const formattedErrors: FieldErrors = {};
    Object.entries(result.error.formErrors.fieldErrors).forEach(
      ([key, value]) => {
        formattedErrors[key] = value || [];
      }
    );
    return {
      data: null,
      errors: formattedErrors,
    };
  }
}

// Resize an image to a max width and re-encode as webp (matches the firebase
// upload path's compression). Shared by the firebase and supabase upload paths.
async function resizeToWebp(file: File, maxWidth = 1024): Promise<Blob> {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const bitmap = await createImageBitmap(file);
  const { width, height } = bitmap;
  const ratio = maxWidth / width;
  canvas.width = maxWidth;
  canvas.height = height * ratio;
  ctx?.drawImage(bitmap, 0, 0, width, height, 0, 0, maxWidth, height * ratio);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/webp", 0.8);
  });
  if (!blob) {
    throw new Error("Failed to create image blob");
  }
  return blob;
}

// Upload an image to Supabase via the backend, through the /api/upload Remix
// proxy (which binds the authenticated profile_id server-side). Returns the
// public URL — same contract as uploadImageToFirebase, so callers only swap
// which function they call (gated by the flirtybum_supabase_upload_v1 account
// feature flag, read in the _app loader as `supabaseUpload`).
export async function uploadImageToSupabase(
  file: File,
  folder: "profiles-firebase" | "chat-attachments-firebase"
): Promise<string> {
  const blob = await resizeToWebp(file);

  const formData = new FormData();
  formData.append("file", blob, `${Date.now()}.webp`);
  formData.append("folder", folder);

  const res = await fetch("/api/upload", { method: "POST", body: formData });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`Supabase upload failed: ${res.status} ${body}`);
  }
  const data = (await res.json()) as { url?: string };
  if (!data.url) {
    throw new Error("Supabase upload returned no url");
  }
  return data.url;
}

export async function uploadImageToFirebase(
  file: File,
  userId: string,
  storagePath: string // e.g., "images/users/userId/gallery" or "images/users/userId/attachments"
): Promise<string> {
  try {
    const canvas = document.createElement("canvas");
    const maxWidth = 1024; // Increased max width for better quality gallery photos

    const ctx = canvas.getContext("2d");
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;

    const ratio = maxWidth / width;
    canvas.width = maxWidth;
    canvas.height = height * ratio;

    ctx?.drawImage(
      bitmap,
      0,
      0,
      width,
      height,
      0,
      0,
      maxWidth,
      height * ratio
    );

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        "image/webp",
        0.8
      );
    });

    if (!blob) {
      throw new Error("Failed to create image blob");
    }

    const storage = getStorage();
    const storageRef = ref(
      storage,
      `${storagePath}/${Date.now()}-${file.name}`
    );
    const snapshot = await uploadBytes(storageRef, blob);
    const url = await getDownloadURL(snapshot.ref);

    return url;
  } catch (error) {
    console.error("Error uploading image to Firebase:", error);
    throw error;
  }
}

export function decodeEmail(email: string): string {
  try {
    const decoded = atob(email);
    return decoded;
  } catch (error) {
    return email; // Return original email if decoding fails
  }
}
